/**
 * 한국어 날짜·시각 표기. 시안의 표기를 그대로 재현한다.
 *
 * Intl에 맡기면 "오후 3:42"가 아니라 "오후 3:42:00"이나 "PM 3:42"가 나오는
 * 경우가 있어(브라우저·로케일 데이터 편차) 시각만 직접 조립한다.
 */

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"] as const;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** 두 시각이 며칠 차이인지. 오늘=0, 어제=1 */
function dayDiff(target: Date, now: Date): number {
  const MS_PER_DAY = 86_400_000;
  return Math.round((startOfDay(now) - startOfDay(target)) / MS_PER_DAY);
}

/** "오전 10:12" / "오후 3:42" */
export function formatTime(iso: string): string {
  const date = new Date(iso);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const meridiem = hours < 12 ? "오전" : "오후";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${meridiem} ${hour12}:${minutes}`;
}

/**
 * 방 목록 오른쪽에 붙는 시각.
 * 오늘이면 시각, 어제면 "어제", 올해면 "7월 28일", 그 전이면 "2025년 7월 28일".
 */
export function formatRoomTimestamp(
  iso: string | null,
  now: Date = new Date(),
): string {
  if (!iso) return "";

  const date = new Date(iso);
  const diff = dayDiff(date, now);

  if (diff === 0) return formatTime(iso);
  if (diff === 1) return "어제";

  const month = date.getMonth() + 1;
  const day = date.getDate();

  return date.getFullYear() === now.getFullYear()
    ? `${month}월 ${day}일`
    : `${date.getFullYear()}년 ${month}월 ${day}일`;
}

/** 대화 가운데 뜨는 날짜 칩. "8월 5일 화요일", 오늘이면 "오늘" */
export function formatDateChip(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const diff = dayDiff(date, now);

  if (diff === 0) return "오늘";
  if (diff === 1) return "어제";

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekday = WEEKDAYS[date.getDay()] ?? "";
  const yearPrefix =
    date.getFullYear() === now.getFullYear() ? "" : `${date.getFullYear()}년 `;

  return `${yearPrefix}${month}월 ${day}일 ${weekday}요일`;
}

/** 두 시각이 같은 날인가. 날짜 칩을 어디에 끼울지 정할 때 쓴다. */
export function isSameDay(a: string, b: string): boolean {
  return startOfDay(new Date(a)) === startOfDay(new Date(b));
}

/** "4.2MB" — 첨부 카드에 붙는 크기 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;

  const units = ["KB", "MB", "GB"] as const;
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded}${units[unitIndex]}`;
}
